/** The kinds of work a booking link can ask for, e.g. `#book/AB12CDE/mot`. */
export type DeepLinkServiceKey = 'mot' | 'service' | 'tyres' | 'repair' | 'diagnostics'

export const DEEP_LINK_SERVICE_KEYS: readonly DeepLinkServiceKey[] = [
  'mot',
  'service',
  'tyres',
  'repair',
  'diagnostics',
]

export function isDeepLinkServiceKey(value: unknown): value is DeepLinkServiceKey {
  return (
    typeof value === 'string' && (DEEP_LINK_SERVICE_KEYS as readonly string[]).includes(value)
  )
}

export interface ExternalBookingRequest {
  shouldOpen: boolean
  registration: string | null
  /** The work the link asked for. Null when it named none, or one we do not know. */
  serviceKey: DeepLinkServiceKey | null
}

/**
 * A plate from an address, uppercased, or null when it can't be one: 2 to 8
 * letters and digits with at least one digit, spaces and dashes allowed. The
 * widget only ever looks a plate up, which is free, and never books from it.
 */
export function linkedRegistration(raw: string | null | undefined): string | null {
  const plate = (raw ?? '').toUpperCase().replace(/[\s-]/g, '')
  return /^[A-Z0-9]{2,8}$/.test(plate) && /\d/.test(plate) ? plate : null
}

function decodeSegment(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/**
 * Parses a host-page URL for a booking deep link.
 *
 * MOT reminder texts link to `#book/{VRM}/{serviceKey}` on the garage's own
 * site, so the hash works on any page with the widget on it. Garages also link
 * to the older forms from their own "book now" buttons and marketing emails, so
 * every shape stays supported:
 *
 *   /#book                 /#book/BA22NZY         /#book/BA22NZY/mot
 *   /#booking?BA22NZY      /#booking?vrm=BA22NZY
 *   /#booking/BA22NZY      /?vrm=BA22NZY
 */
export function parseExternalBookingRequest(url: URL): ExternalBookingRequest {
  const fromQuery =
    url.searchParams.get('vrm') ??
    url.searchParams.get('reg') ??
    url.searchParams.get('registration')

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash
  const [hashRouteRaw, hashQueryRaw = ''] = hash.split('?', 2)
  const segments = hashRouteRaw.split('/').map((segment) => decodeSegment(segment).trim())
  const route = (segments[0] ?? '').toLowerCase()

  const isBook = route === 'book'
  const isBooking = route === 'booking'
  const shouldOpen = isBook || isBooking

  // `#booking/AB12/CDE` has always been read as one plate, so the older form
  // keeps joining what follows; `#book` reserves its third segment for the service.
  const hashPathVrm = isBook ? (segments[1] ?? '') : isBooking ? segments.slice(1).join('') : ''
  const rawServiceKey = isBook ? (segments[2] ?? '').toLowerCase() : ''

  let fromHash: string | null = null
  if (hashQueryRaw) {
    const hashQuery = new URLSearchParams(hashQueryRaw)
    fromHash =
      hashQuery.get('vrm') ?? hashQuery.get('reg') ?? hashQuery.get('registration') ?? null

    // `#booking?AB12CDE`: a bare value with no key.
    if (!fromHash && !hashQueryRaw.includes('=')) {
      fromHash = hashQueryRaw
    }
  }

  return {
    shouldOpen,
    registration: linkedRegistration(fromHash || hashPathVrm || fromQuery),
    serviceKey: isDeepLinkServiceKey(rawServiceKey) ? rawServiceKey : null,
  }
}

export interface PaymentReturn {
  isReturn: boolean
  cancelled: boolean
  sessionId: string | null
  bookingId: string | null
}

/** Reads the query params Stripe Checkout sends us back with. */
export function parsePaymentReturn(url: URL): PaymentReturn {
  const params = url.searchParams
  if (params.get('ac_src') !== 'widget') {
    return { isReturn: false, cancelled: false, sessionId: null, bookingId: null }
  }

  return {
    isReturn: true,
    cancelled: params.get('ac_cancel') === '1',
    sessionId: params.get('session_id'),
    bookingId: params.get('booking_id'),
  }
}

const WIDGET_PARAMS = ['ac_success', 'ac_cancel', 'ac_src', 'booking_id', 'session_id']

/** Strips our own params so the garage's URL is clean after a payment round-trip. */
export function stripWidgetParams(href: string): string {
  try {
    const url = new URL(href)
    for (const param of WIDGET_PARAMS) url.searchParams.delete(param)
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return href
  }
}

/**
 * The page Stripe should send the customer back to: the page the widget is
 * embedded on, without our own params and without the hash. The hash goes
 * because a `#book/...` link would otherwise run again over the confirmation.
 *
 * Null when the page is not an http(s) page; the backend then falls back to
 * the channel's allowed origin.
 */
export function buildPaymentReturnUrl(href: string): string | null {
  try {
    const url = new URL(href)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null

    for (const param of WIDGET_PARAMS) url.searchParams.delete(param)
    url.hash = ''
    return url.toString()
  } catch {
    return null
  }
}
